import { startOfDay } from "date-fns";
import { useMemo } from "react";
import type { TimelineItemResponseDTO as TimelineItem } from "web_myplann/Infrastructure/Api/Api";
import { TimelineHorizonEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import {
	computeTimeBuckets,
	type UpcomingBucketKey,
} from "web_myplann/NotificationCenter/Utils/computeTimeBuckets";
import { sortTimelineItems } from "web_myplann/NotificationCenter/Utils/sortTimelineItems";
import { formatApiDate } from "web_myplann/Shared/Utils/apiDate";

type BucketLabelResource = (typeof Resources.NotificationCenter.UpcomingGroups)[
	| "In7Days"
	| "In14Days"
	| "In30Days"
	| "In3Months"
	| "In6Months"
	| "In12Months"];

export type UpcomingGroup = {
	bucketKey: UpcomingBucketKey;
	labelResource: BucketLabelResource;
	items: TimelineItem[];
	visualWeight: "normal" | "muted";
};

const HORIZON_BUCKETS: Record<TimelineHorizonEnum, UpcomingBucketKey[]> = {
	[TimelineHorizonEnum.Month]: ["in7days", "in14days", "in30days"],
	[TimelineHorizonEnum.HalfYear]: [
		"in7days",
		"in14days",
		"in30days",
		"in3months",
		"in6months",
	],
	[TimelineHorizonEnum.Year]: [
		"in7days",
		"in14days",
		"in30days",
		"in3months",
		"in6months",
		"in12months",
	],
};

const TIME_GROUP_ORDER: UpcomingBucketKey[] = [
	"in7days",
	"in14days",
	"in30days",
	"in3months",
	"in6months",
	"in12months",
];

const MUTED_BUCKETS = new Set<UpcomingBucketKey>([
	"in3months",
	"in6months",
	"in12months",
]);

const BUCKET_LABEL_RESOURCES: Record<UpcomingBucketKey, BucketLabelResource> = {
	in7days: Resources.NotificationCenter.UpcomingGroups.In7Days,
	in14days: Resources.NotificationCenter.UpcomingGroups.In14Days,
	in30days: Resources.NotificationCenter.UpcomingGroups.In30Days,
	in3months: Resources.NotificationCenter.UpcomingGroups.In3Months,
	in6months: Resources.NotificationCenter.UpcomingGroups.In6Months,
	in12months: Resources.NotificationCenter.UpcomingGroups.In12Months,
};

export const useGroupUpcomingItems = (
	items: TimelineItem[],
	horizon: TimelineHorizonEnum,
): UpcomingGroup[] => {
	const todayKey = formatApiDate(new Date());
	return useMemo(() => {
		const today = startOfDay(new Date());
		const allowed = new Set(HORIZON_BUCKETS[horizon]);

		const byBucket = new Map<UpcomingBucketKey, TimelineItem[]>();

		for (const item of items) {
			const bucket = computeTimeBuckets(item, today);
			if (bucket === null || !allowed.has(bucket)) {
				continue;
			}
			const list = byBucket.get(bucket) ?? [];
			list.push(item);
			byBucket.set(bucket, list);
		}

		const timeGroups: UpcomingGroup[] = [];
		for (const bucket of TIME_GROUP_ORDER) {
			if (!allowed.has(bucket)) continue;
			const bucketItems = byBucket.get(bucket);
			if (!bucketItems || bucketItems.length === 0) continue;
			timeGroups.push({
				bucketKey: bucket,
				labelResource: BUCKET_LABEL_RESOURCES[bucket],
				items: sortTimelineItems(bucketItems),
				visualWeight: MUTED_BUCKETS.has(bucket) ? "muted" : "normal",
			});
		}

		return timeGroups;
	}, [items, horizon, todayKey]);
};
